import { useState } from "react";
import { firebaseConfigured, signInWithGoogle } from "../lib/firebase";

/**
 * The sign-in screen for Firebase mode. Everything animates with plain CSS
 * (keyframes in index.css): drifting gradient orbs behind a frosted card,
 * staggered fade-ups, and a slow-breathing wordmark.
 */
export default function FirebaseLogin() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleGoogle() {
    setError(null);
    setBusy(true);
    try {
      await signInWithGoogle();
      // onAuthStateChanged in App.tsx takes it from here.
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      // The user closing the popup is not an error worth shouting about.
      if (!/popup-closed|cancelled-popup/.test(message)) {
        setError("Sign-in didn't complete. Please try again.");
      }
      setBusy(false);
    }
  }

  return (
    <div className="login-scene relative flex min-h-screen items-center justify-center overflow-hidden bg-bg px-4 text-text">
      {/* drifting gradient orbs */}
      <div aria-hidden="true" className="login-orb login-orb-1" />
      <div aria-hidden="true" className="login-orb login-orb-2" />
      <div aria-hidden="true" className="login-orb login-orb-3" />

      <div className="login-card card-shadow relative w-full max-w-sm rounded-2xl border border-border bg-surface/80 p-8 text-center backdrop-blur-xl">
        <p className="login-rise font-serif text-4xl font-semibold tracking-tight" style={{ animationDelay: "0.05s" }}>
          Boort
        </p>
        <p className="login-rise mt-3 text-sm leading-relaxed text-muted" style={{ animationDelay: "0.15s" }}>
          Turn any YouTube video into a paper worth reading - full-fidelity or sized to your time.
        </p>

        <div className="login-rise mt-8" style={{ animationDelay: "0.25s" }}>
          {firebaseConfigured ? (
            <button
              type="button"
              onClick={handleGoogle}
              disabled={busy}
              className="login-google-btn inline-flex w-full items-center justify-center gap-3 rounded-lg border border-border bg-bg px-5 py-3 text-sm font-medium text-text transition-all hover:-translate-y-0.5 hover:border-accent disabled:opacity-60"
            >
              <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
                <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
                <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
                <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
                <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
              </svg>
              {busy ? "Opening Google…" : "Continue with Google"}
            </button>
          ) : (
            <p className="rounded-lg border border-border bg-bg px-4 py-3 text-xs text-muted">
              Sign-in is enabled on the server, but this build is missing its Firebase client config
              (VITE_FIREBASE_API_KEY / VITE_FIREBASE_PROJECT_ID).
            </p>
          )}
          {error ? (
            <p className="mt-3 text-sm text-danger" role="alert">
              {error}
            </p>
          ) : null}
        </div>

        <p className="login-rise mt-8 text-xs text-muted" style={{ animationDelay: "0.35s" }}>
          Your Google account is only used to sign you in.
        </p>
      </div>
    </div>
  );
}
